import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Material, Category } from '../types/index';

export class MaterialService {
  /**
   * Create a new material
   */
  static async createMaterial(material: Omit<Material, 'id' | 'createdAt' | 'updatedAt'>): Promise<Material> {
    try {
      const materialRef = doc(collection(db, 'materials'));
      const now = new Date();

      const materialData: any = {
        ...material,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(materialRef, materialData);

      return {
        ...material,
        id: materialRef.id,
        createdAt: now,
        updatedAt: now,
      };
    } catch (error: any) {
      throw new Error(`Failed to create material: ${error.message}`);
    }
  }

  /**
   * Get a single material by ID
   */
  static async getMaterial(id: string): Promise<Material | null> {
    try {
      const docSnap = await getDoc(doc(db, 'materials', id));
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          id: docSnap.id,
          createdAt: data.createdAt ? new Date(data.createdAt.toMillis()) : new Date(),
          updatedAt: data.updatedAt ? new Date(data.updatedAt.toMillis()) : new Date(),
        } as Material;
      }
      return null;
    } catch (error: any) {
      throw new Error(`Failed to get material: ${error.message}`);
    }
  }

  /**
   * Get all materials
   */
  static async getAllMaterials(): Promise<Material[]> {
    try {
      const q = query(
        collection(db, 'materials'),
        orderBy('name', 'asc')
      );
      const querySnap = await getDocs(q);
      return querySnap.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          createdAt: data.createdAt ? new Date(data.createdAt.toMillis()) : new Date(),
          updatedAt: data.updatedAt ? new Date(data.updatedAt.toMillis()) : new Date(),
        } as Material;
      });
    } catch (error: any) {
      throw new Error(`Failed to get materials: ${error.message}`);
    }
  }

  /**
   * Get materials by category
   */
  static async getMaterialsByCategory(categoryId: string): Promise<Material[]> {
    try {
      const q = query(
        collection(db, 'materials'),
        where('categoryId', '==', categoryId),
        orderBy('name', 'asc')
      );
      const querySnap = await getDocs(q);
      return querySnap.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          createdAt: data.createdAt ? new Date(data.createdAt.toMillis()) : new Date(),
          updatedAt: data.updatedAt ? new Date(data.updatedAt.toMillis()) : new Date(),
        } as Material;
      });
    } catch (error: any) {
      throw new Error(`Failed to get materials by category: ${error.message}`);
    }
  }

  /**
   * Update a material
   */
  static async updateMaterial(id: string, updates: Partial<Material>): Promise<void> {
    try {
      const { id: _id, createdAt, updatedAt, ...dataToUpdate } = updates as any;
      await updateDoc(doc(db, 'materials', id), {
        ...dataToUpdate,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to update material: ${error.message}`);
    }
  }

  /**
   * Delete a material
   */
  static async deleteMaterial(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'materials', id));
    } catch (error: any) {
      throw new Error(`Failed to delete material: ${error.message}`);
    }
  }

  /**
   * Get or create all categories
   */
  static async getCategories(): Promise<Category[]> {
    try {
      const querySnap = await getDocs(
        query(collection(db, 'categories'), orderBy('order', 'asc'))
      );

      let categories = querySnap.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      } as Category));

      // If no categories exist, initialize default ones
      if (categories.length === 0) {
        await this.initializeDefaultCategories();
        const updatedSnap = await getDocs(
          query(collection(db, 'categories'), orderBy('order', 'asc'))
        );
        categories = updatedSnap.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        } as Category));
      }

      return categories;
    } catch (error: any) {
      throw new Error(`Failed to get categories: ${error.message}`);
    }
  }

  /**
   * Create a new category
   */
  static async createCategory(category: Omit<Category, 'id'>): Promise<Category> {
    try {
      const categoryRef = doc(collection(db, 'categories'));
      await setDoc(categoryRef, category);
      return {
        ...category,
        id: categoryRef.id,
      };
    } catch (error: any) {
      throw new Error(`Failed to create category: ${error.message}`);
    }
  }

  /**
   * Delete a category
   */
  static async deleteCategory(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'categories', id));
    } catch (error: any) {
      throw new Error(`Failed to delete category: ${error.message}`);
    }
  }

  /**
   * Initialize default categories if they don't exist
   */
  private static async initializeDefaultCategories(): Promise<void> {
    const defaultCategories: Omit<Category, 'id'>[] = [
      { name: 'Wood', parentCategoryId: null, order: 1 },
      { name: 'PVC', parentCategoryId: null, order: 2 },
      { name: 'Acrylic', parentCategoryId: null, order: 3 },
      { name: 'Composite', parentCategoryId: null, order: 4 },
      { name: 'Coroplast', parentCategoryId: null, order: 5 },
      { name: 'Diebond', parentCategoryId: null, order: 6 },
      { name: 'Durabond', parentCategoryId: null, order: 7 },
      { name: 'Foam Board', parentCategoryId: null, order: 8 },
      { name: 'Ultraboard', parentCategoryId: null, order: 9 },
    ];

    const categoriesRef = collection(db, 'categories');

    for (const category of defaultCategories) {
      const docRef = doc(categoriesRef);
      await setDoc(docRef, category);
    }
  }
}
